import React, { useState } from 'react';
import { 
  ShieldCheck, 
  Lock, 
  Key, 
  AlertTriangle, 
  CheckCircle2, 
  Mail, 
  Smartphone, 
  Building2, 
  Users, 
  Share2, 
  ExternalLink,
  Info,
  Sparkles,
  ShieldAlert
} from 'lucide-react';
import { StatusIndicator } from '../components/common/StatusIndicator';
import { SecurityStatus } from '../types';

export const AccountSecurityPage: React.FC = () => {
  const [checklist, setChecklist] = useState<Record<string, boolean>>({
    mfa_email: true,
    mfa_bank: true,
    mfa_mygov: true,
    passphrase_super: false,
    review_recovery_phone: true,
    remove_dormant_apps: false
  });

  const toggleItem = (key: string) => {
    setChecklist(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const accountAreas: {
    name: string;
    icon: any;
    status: SecurityStatus;
    statusLabel: string;
    details: string;
    actionNote: string;
  }[] = [
    {
      name: 'Email Accounts (Primary Google & Outlook)',
      icon: Mail,
      status: 'good',
      statusLabel: 'Secure',
      details: 'App-based 2FA active. No unauthorized forwarding rules or foreign sessions found.',
      actionNote: 'Audit connected OAuth third-party apps quarterly.'
    },
    {
      name: 'Australian Banking & Cards (NetBank, Westpac, NAB)',
      icon: Building2,
      status: 'good',
      statusLabel: 'Secure',
      details: 'SMS/App NetCode enabled. Instant push notifications active on all debits.',
      actionNote: 'Maintain verbal phone security passwords on banking profiles.'
    },
    {
      name: 'Google & Apple Ecosystem Accounts',
      icon: Smartphone,
      status: 'attention',
      statusLabel: 'Review',
      details: 'Passkeys registered on primary Mac/iPhone. Secondary iPad pending biometric sync.',
      actionNote: 'Review recovery phone number and trusted device list.'
    },
    {
      name: 'Social Media & Marketplaces (Meta, LinkedIn, Gumtree)',
      icon: Share2,
      status: 'risk',
      statusLabel: 'Weak',
      details: 'Reused password detected on legacy marketplace accounts. 2FA not enabled on Facebook.',
      actionNote: 'Enable 2FA immediately and replace passwords with unique passphrases.'
    },
    {
      name: 'Australian Government Accounts (myGov, ATO, Medicare)',
      icon: Lock,
      status: 'good',
      statusLabel: 'Secure',
      details: 'Connected via myGovID / Digital Identity App with High Identity Strength.',
      actionNote: 'Never click tax refund links in unsolicited emails.'
    }
  ];

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-10">
      {/* Header */}
      <div className="text-center space-y-3">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-950 text-cyan-300 text-xs font-semibold border border-cyan-800">
          <ShieldCheck className="w-3.5 h-3.5" />
          <span>Account & Credential Security Audit</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-extrabold text-white">
          Secure Your Essential Digital Accounts
        </h1>
        <p className="text-slate-400 text-sm max-w-xl mx-auto">
          Audit the security posture of your Australian email, banking, myGov, and social accounts to prevent unauthorized account takeovers.
        </p>
      </div>

      {/* Mandatory Security Notice */}
      <div className="p-4 rounded-2xl bg-emerald-950/40 border border-emerald-800/80 flex items-center gap-3 text-xs text-emerald-200">
        <Lock className="w-5 h-5 text-emerald-400 flex-shrink-0" />
        <div>
          <strong className="font-bold text-white">Privacy & Security Commitment: </strong>
          We will <span className="underline font-bold">never</span> ask you to enter or submit your actual account passwords into IDFraud.au.
        </div>
      </div>

      {/* Account Security Audit Table (Section 16) */}
      <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 shadow-2xl space-y-6">
        <div className="flex items-center justify-between border-b border-slate-800 pb-4">
          <div>
            <h3 className="text-base font-bold text-white">Account Security Status Matrix</h3>
            <p className="text-xs text-slate-400">Current status across key identity domains</p>
          </div>
          <span className="text-xs font-mono text-emerald-400 font-bold bg-slate-950 px-3 py-1 rounded-lg border border-slate-800">
            8 / 10 Accounts Secured
          </span>
        </div>

        <div className="divide-y divide-slate-800/80">
          {accountAreas.map((area, idx) => {
            const Icon = area.icon;
            return (
              <div key={idx} className="py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-start gap-3.5">
                  <div className="p-2.5 rounded-xl bg-slate-950 border border-slate-800 text-slate-300 mt-0.5">
                    <Icon className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <h4 className="text-sm font-bold text-white">{area.name}</h4>
                      <StatusIndicator status={area.status} showLabel />
                    </div>
                    <p className="text-xs text-slate-300 leading-snug">{area.details}</p>
                    <p className="text-[11px] text-slate-500 font-medium">Recommendation: {area.actionNote}</p>
                  </div>
                </div>

                <div className="self-end sm:self-center">
                  <span className={`px-3 py-1 rounded-lg text-xs font-bold ${
                    area.status === 'good' ? 'bg-emerald-950 text-emerald-300 border border-emerald-800' :
                    area.status === 'attention' ? 'bg-amber-950 text-amber-300 border border-amber-800' :
                    'bg-rose-950 text-rose-300 border border-rose-800'
                  }`}>
                    {area.statusLabel}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Recommended Action Checklist */}
      <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 space-y-6 shadow-xl">
        <h3 className="text-base font-bold text-white flex items-center gap-2">
          <Key className="w-5 h-5 text-emerald-400" />
          <span>Priority Security Checklist</span>
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
          <div
            onClick={() => toggleItem('mfa_email')}
            className={`p-4 rounded-xl border cursor-pointer transition-all flex items-start gap-3 ${
              checklist.mfa_email ? 'bg-emerald-950/30 border-emerald-700/60 text-slate-200' : 'bg-slate-950 border-slate-800 text-slate-400'
            }`}
          >
            <CheckCircle2 className={`w-4 h-4 flex-shrink-0 mt-0.5 ${checklist.mfa_email ? 'text-emerald-400' : 'text-slate-600'}`} />
            <div>
              <div className="font-bold text-white">Enable App MFA on Primary Email</div>
              <div className="text-[11px] text-slate-400 mt-0.5">Stops credential stuffing and password reset hijacks.</div>
            </div>
          </div>

          <div
            onClick={() => toggleItem('passphrase_super')}
            className={`p-4 rounded-xl border cursor-pointer transition-all flex items-start gap-3 ${
              checklist.passphrase_super ? 'bg-emerald-950/30 border-emerald-700/60 text-slate-200' : 'bg-slate-950 border-slate-800 text-slate-400'
            }`}
          >
            <CheckCircle2 className={`w-4 h-4 flex-shrink-0 mt-0.5 ${checklist.passphrase_super ? 'text-emerald-400' : 'text-slate-600'}`} />
            <div>
              <div className="font-bold text-white">Change Reused Passwords</div>
              <div className="text-[11px] text-slate-400 mt-0.5">Adopt 4-word passphrases or a password manager.</div>
            </div>
          </div>

          <div
            onClick={() => toggleItem('review_recovery_phone')}
            className={`p-4 rounded-xl border cursor-pointer transition-all flex items-start gap-3 ${
              checklist.review_recovery_phone ? 'bg-emerald-950/30 border-emerald-700/60 text-slate-200' : 'bg-slate-950 border-slate-800 text-slate-400'
            }`}
          >
            <CheckCircle2 className={`w-4 h-4 flex-shrink-0 mt-0.5 ${checklist.review_recovery_phone ? 'text-emerald-400' : 'text-slate-600'}`} />
            <div>
              <div className="font-bold text-white">Review Recovery Phone & Email</div>
              <div className="text-[11px] text-slate-400 mt-0.5">Ensure backup contacts on Google and Apple are current.</div>
            </div>
          </div>

          <div
            onClick={() => toggleItem('remove_dormant_apps')}
            className={`p-4 rounded-xl border cursor-pointer transition-all flex items-start gap-3 ${
              checklist.remove_dormant_apps ? 'bg-emerald-950/30 border-emerald-700/60 text-slate-200' : 'bg-slate-950 border-slate-800 text-slate-400'
            }`}
          >
            <CheckCircle2 className={`w-4 h-4 flex-shrink-0 mt-0.5 ${checklist.remove_dormant_apps ? 'text-emerald-400' : 'text-slate-600'}`} />
            <div>
              <div className="font-bold text-white">Remove Dormant Old Accounts</div>
              <div className="text-[11px] text-slate-400 mt-0.5">Delete unused accounts to shrink your exposure attack surface.</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
